import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Paper, Typography, IconButton, Tooltip, useTheme, alpha, Divider, Chip, useMediaQuery,
  Menu, MenuItem, ListItemIcon, ListItemText,
} from '@mui/material';
import {
  ZoomIn, ZoomOut, CenterFocusStrong, Park as TreeIcon,
  CenterFocusWeak as FocusIcon, Close as ExitFocusIcon,
  SaveAlt as ExportIcon, Fullscreen as FullscreenIcon,
  FullscreenExit as FullscreenExitIcon,
  AccountTree as LayoutIcon, DensitySmall as CompactIcon,
  ViewColumn as VerticalIcon, FormatListBulleted as ListIcon, Check as CheckIcon,
} from '@mui/icons-material';
import { Membre, Relation, TreeNode } from '../types';
import { genColor, fullDate, shortDate, yearsRange } from '../utils/treeVisuals';

// ─── Layout config ────────────────────────────────────────────────────────────
export type LayoutMode = 'horizontal' | 'compact' | 'vertical' | 'indented';

const CARD_H = 80;        // hauteur d'une carte (constante dans tous les modes)
const COUPLE_VGAP = 30;   // espace vertical entre les 2 cartes d'un couple empilé (mode vertical)

interface SpaceCfg { NODE_W: number; COUPLE_W: number; CARD_W: number; H_GAP: number; V_GAP: number; TREE_GAP: number; }
const SPACE: Record<'horizontal' | 'compact' | 'vertical', SpaceCfg> = {
  horizontal: { NODE_W: 178, COUPLE_W: 342, CARD_W: 161, H_GAP: 48, V_GAP: 100, TREE_GAP: 80 },
  compact:    { NODE_W: 150, COUPLE_W: 306, CARD_W: 144, H_GAP: 14, V_GAP: 60,  TREE_GAP: 38 },
  vertical:   { NODE_W: 178, COUPLE_W: 342, CARD_W: 161, H_GAP: 78, V_GAP: 20,  TREE_GAP: 34 },
};

// ─── Types ────────────────────────────────────────────────────────────────────
interface LayoutNode {
  id: string | number;
  fx: number; fy: number;   // coin haut-gauche de la boîte
  w: number; h: number;
  treeNode: TreeNode;
  isCouple: boolean;
  depth: number;
}

interface EdgeGroup {
  px: number; py: number;                  // point de sortie du parent
  children: { x: number; y: number }[];    // points d'entrée des enfants
}

// ─── Layout algorithm (orienté : horizontal haut→bas, ou vertical gauche→droite) ─
function computeLayout(roots: TreeNode[], mode: LayoutMode) {
  const cfg = SPACE[mode === 'compact' ? 'compact' : mode === 'vertical' ? 'vertical' : 'horizontal'];
  const LR = mode === 'vertical';                       // left→right
  const coupleMain = LR ? (2 * CARD_H + COUPLE_VGAP) : cfg.COUPLE_W;

  // mainSize = extent le long de l'axe des fratries ; crossSize = épaisseur le long de l'axe des générations
  const mainSize  = (n: TreeNode) => n.attributes?.isCouple ? coupleMain : (LR ? CARD_H : cfg.NODE_W);
  const crossSize = (n: TreeNode) => LR ? (n.attributes?.isCouple ? cfg.CARD_W : cfg.NODE_W) : CARD_H;
  const sibGap = LR ? cfg.V_GAP : cfg.H_GAP;
  const step   = LR ? (cfg.NODE_W + cfg.H_GAP) : (CARD_H + cfg.V_GAP);

  const extent = (n: TreeNode): number => {
    const own = mainSize(n) + sibGap;
    if (!n.children?.length) return own;
    return Math.max(own, n.children.reduce((s, c) => s + extent(c), 0));
  };

  const nodes: LayoutNode[] = [];
  const edges: EdgeGroup[] = [];

  const place = (n: TreeNode, depth: number, offMain: number, parent?: { x: number; y: number }) => {
    const e = extent(n);
    const mainPos = offMain + e / 2;
    const cross = depth * step;
    const w = LR ? crossSize(n) : mainSize(n);
    const h = LR ? mainSize(n) : CARD_H;
    const fx = LR ? cross : mainPos - w / 2;
    const fy = LR ? mainPos - h / 2 : cross;
    const isCouple = !!n.attributes?.isCouple;
    nodes.push({ id: n.id, fx, fy, w, h, treeNode: n, isCouple, depth });

    if (parent) {
      // point d'entrée de l'enfant (ciblé sur la bonne carte si couple)
      let ix = LR ? fx : fx + w / 2;
      let iy = LR ? fy + h / 2 : fy;
      if (isCouple && n.attributes?.childPersonId) {
        const p1Id = n.attributes.person1?.attributes?.membreId ?? Number(n.attributes.person1?.id);
        const first = p1Id === n.attributes.childPersonId; // person1 = gauche (H) / haut (V)
        if (LR) iy = fy + (first ? CARD_H / 2 : h - CARD_H / 2);
        else ix = fx + (first ? cfg.CARD_W / 2 : w - cfg.CARD_W / 2);
      }
      const g = edges.find(gr => gr.px === parent.x && gr.py === parent.y);
      if (g) g.children.push({ x: ix, y: iy });
      else edges.push({ px: parent.x, py: parent.y, children: [{ x: ix, y: iy }] });
    }

    if (n.children?.length) {
      const out = { x: LR ? fx + w : fx + w / 2, y: LR ? fy + h / 2 : fy + h };
      let childOff = offMain;
      n.children.forEach(c => { place(c, depth + 1, childOff, out); childOff += extent(c); });
    }
  };

  let off = 0;
  roots.forEach(r => { place(r, 0, off); off += extent(r) + cfg.TREE_GAP; });

  const maxX = nodes.length ? Math.max(...nodes.map(n => n.fx + n.w)) : 1;
  const maxY = nodes.length ? Math.max(...nodes.map(n => n.fy + n.h)) : 1;
  return { nodes, edges, totalWidth: Math.max(maxX, 1) + 40, totalHeight: Math.max(maxY, 1) + 40, LR };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const genderColor = (sexe?: string) => {
  if (sexe === 'Homme') return '#3B82F6';
  if (sexe === 'Femme') return '#EC4899';
  return '#6B7280';
};

const initials = (name: string) => {
  const p = name.trim().split(' ');
  return p.length >= 2 ? `${p[0][0]}${p[p.length - 1][0]}`.toUpperCase() : name.substring(0, 2).toUpperCase();
};

// ─── Person card ──────────────────────────────────────────────────────────────
interface CardProps {
  name: string;
  sexe?: string;
  prenom_naissance?: string;
  prenom_naissance_accord?: string;
  date_naissance?: string;
  date_deces?: string;
  statut_marital?: string;
  photo?: string;
  selected: boolean;
  lineageHighlighted: boolean;
  isDark: boolean;
  primaryColor: string;
  genDepth: number;
  onClick: () => void;
  onDoubleClick?: () => void;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  width: number;
}

const PersonCardDiv: React.FC<CardProps> = ({
  name, sexe, prenom_naissance, prenom_naissance_accord, date_naissance, date_deces, statut_marital, photo,
  selected, lineageHighlighted, isDark, primaryColor, genDepth,
  onClick, onDoubleClick, onMouseEnter, onMouseLeave, width,
}) => {
  const color = genderColor(sexe);
  const deceased = !!date_deces;
  const gc = genColor(genDepth);

  const bg = isDark
    ? (deceased ? '#1C1C1C' : '#1A2E1A')
    : (deceased ? '#F8F8F8' : '#FFFFFF');
  const border = selected
    ? primaryColor
    : lineageHighlighted
      ? '#F59E0B'
      : isDark ? (deceased ? '#333' : '#1E3A1E') : (deceased ? '#D1D5DB' : '#E5E7EB');
  const shadow = selected
    ? `0 0 0 2px ${primaryColor}55, 0 4px 16px rgba(0,0,0,0.2)`
    : lineageHighlighted
      ? `0 0 0 2px #F59E0B66, 0 2px 8px rgba(0,0,0,0.12)`
      : '0 2px 8px rgba(0,0,0,0.07)';
  const textPrimary  = isDark ? (deceased ? '#9CA3AF' : '#F0FDF4') : (deceased ? '#6B7280' : '#1C1917');
  const textSecondary = isDark ? '#86EFAC' : '#6B7280';
  const naissanceStr = fullDate(date_naissance);
  const decesStr = fullDate(date_deces);

  return (
    <div
      onClick={onClick}
      onDoubleClick={onDoubleClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      style={{
        width, height: CARD_H,
        background: bg,
        border: `1.5px solid ${border}`,
        borderTop: `3px solid ${lineageHighlighted ? '#F59E0B' : gc}`,
        borderRadius: 10,
        display: 'flex', alignItems: 'center',
        boxShadow: shadow,
        overflow: 'hidden',
        cursor: 'pointer', boxSizing: 'border-box',
        transition: 'box-shadow 0.15s, border-color 0.15s',
        opacity: deceased ? 0.82 : 1,
      }}
    >
      {/* Gender strip */}
      <div style={{ width: 4, alignSelf: 'stretch', background: deceased ? '#9CA3AF' : color, flexShrink: 0 }} />
      {/* Avatar */}
      <div style={{
        width: 28, height: 28, borderRadius: '50%', flexShrink: 0,
        background: photo ? 'transparent' : (deceased ? 'rgba(156,163,175,0.15)' : `${color}22`),
        color: deceased ? '#9CA3AF' : color,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 800, fontSize: 11, margin: '0 7px',
        overflow: 'hidden',
      }}>
        {photo
          ? <img src={photo} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : deceased ? '†' : initials(name)
        }
      </div>
      {/* Text */}
      <div style={{ flex: 1, minWidth: 0, paddingRight: 10 }}>
        <div style={{
          fontWeight: 700, fontSize: 12.5, color: textPrimary, lineHeight: 1.2,
          overflow: 'hidden', display: '-webkit-box',
          WebkitLineClamp: (deceased || prenom_naissance) ? 1 : 2, WebkitBoxOrient: 'vertical',
        }}>
          {name}
        </div>
        {prenom_naissance && (
          <div style={{ fontSize: 10, fontStyle: 'italic', color: textSecondary, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {prenom_naissance_accord || 'né·e'} {prenom_naissance}
          </div>
        )}
        {naissanceStr && (
          <div style={{ fontSize: 10, color: textSecondary, marginTop: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            ★ {naissanceStr}
          </div>
        )}
        {decesStr && (
          <div style={{ fontSize: 10, color: textSecondary, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            † {decesStr}
          </div>
        )}
        {statut_marital && !deceased && !naissanceStr && (
          <div style={{ fontSize: 10, color: textSecondary, opacity: 0.75, marginTop: 1 }}>{statut_marital}</div>
        )}
      </div>
    </div>
  );
};

// ─── SVG node renderer ────────────────────────────────────────────────────────
interface NodeRendererProps {
  node: LayoutNode;
  LR: boolean;
  selectedId: number | null;
  lineageIds: Set<number>;
  hoveredId: number | null;
  isDark: boolean;
  primaryColor: string;
  onNodeClick: (id: number) => void;
  onNodeDblClick?: (id: number) => void;
  onHover: (id: number | null) => void;
}

const DatePill: React.FC<{ label: string; isDark: boolean }> = ({ label, isDark }) => (
  <div style={{
    whiteSpace: 'nowrap', fontSize: 9.5, fontWeight: 600, lineHeight: 1.2,
    color: isDark ? '#9CA3AF' : '#4B5563',
    background: isDark ? '#15201A' : '#FFFFFF',
    border: `1px solid ${isDark ? '#244034' : '#E5E7EB'}`,
    borderRadius: 9, padding: '1px 8px',
  }}>
    {label}
  </div>
);

const SvgNode: React.FC<NodeRendererProps> = ({
  node, LR, selectedId, lineageIds, isDark, primaryColor, onNodeClick, onNodeDblClick, onHover,
}) => {
  const { fx, fy, w, h, treeNode, isCouple, depth } = node;

  if (isCouple) {
    const p1 = treeNode.attributes?.person1;
    const p2 = treeNode.attributes?.person2;
    if (!p1 || !p2) return null;
    const id1 = p1.attributes?.membreId ?? Number(p1.id);
    const id2 = p2.attributes?.membreId ?? Number(p2.id);
    const divorced = !!treeNode.attributes?.isDivorced;
    const mariage = shortDate(treeNode.attributes?.marriageDate);
    const divorce = shortDate(treeNode.attributes?.divorceDate);
    const married = !divorced && !!mariage;                  // date de mariage => marié
    const symbol = divorced ? '💔' : married ? '💍' : '❤️';   // sinon (pas de date) => en couple
    const symbolColor = divorced ? '#9CA3AF' : '#F87171';
    const dateLabel = divorced
      ? [mariage && `💍 ${mariage}`, divorce && `💔 ${divorce}`].filter(Boolean).join('   ·   ')
      : married ? `💍 ${mariage}` : '';

    const card = (p: TreeNode, id: number, cardW: number) => (
      <PersonCardDiv
        name={p.name} sexe={p.attributes?.sexe}
        prenom_naissance={p.attributes?.prenom_naissance} prenom_naissance_accord={p.attributes?.prenom_naissance_accord}
        date_naissance={p.attributes?.date_naissance} date_deces={p.attributes?.date_deces}
        photo={p.attributes?.photo}
        selected={selectedId === id} lineageHighlighted={lineageIds.has(id)}
        isDark={isDark} primaryColor={primaryColor} genDepth={depth}
        onClick={() => onNodeClick(id)}
        onDoubleClick={() => onNodeDblClick?.(id)}
        onMouseEnter={() => onHover(id)} onMouseLeave={() => onHover(null)}
        width={cardW}
      />
    );

    if (LR) {
      // Couple empilé verticalement : carte / symbole + date / carte
      return (
        <foreignObject x={fx} y={fy} width={w} height={h} style={{ overflow: 'visible' }} data-nodeclick="true">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: w }}
            onDoubleClick={e => { e.stopPropagation(); }}>
            {card(p1, id1, w)}
            <div style={{ height: COUPLE_VGAP, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <span style={{ fontSize: 14, color: symbolColor, userSelect: 'none' }}>{symbol}</span>
              {dateLabel && <DatePill label={dateLabel} isDark={isDark} />}
            </div>
            {card(p2, id2, w)}
          </div>
        </foreignObject>
      );
    }

    // Couple côte à côte : carte • symbole • carte, date en pastille dessous
    return (
      <foreignObject x={fx} y={fy} width={w} height={h} style={{ overflow: 'visible' }} data-nodeclick="true">
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: w }}
          onDoubleClick={e => { e.stopPropagation(); }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 0, width: w }}>
            {card(p1, id1, (w - 20) / 2)}
            <div style={{ flexShrink: 0, width: 20, textAlign: 'center', userSelect: 'none', fontSize: 14, color: symbolColor, lineHeight: 1 }}>
              {symbol}
            </div>
            {card(p2, id2, (w - 20) / 2)}
          </div>
          {dateLabel && <div style={{ marginTop: 4 }}><DatePill label={dateLabel} isDark={isDark} /></div>}
        </div>
      </foreignObject>
    );
  }

  const membreId = treeNode.attributes?.membreId ?? Number(treeNode.id);
  return (
    <foreignObject x={fx} y={fy} width={w} height={h} style={{ overflow: 'visible' }} data-nodeclick="true">
      <PersonCardDiv
        name={treeNode.name} sexe={treeNode.attributes?.sexe}
        prenom_naissance={treeNode.attributes?.prenom_naissance} prenom_naissance_accord={treeNode.attributes?.prenom_naissance_accord}
        date_naissance={treeNode.attributes?.date_naissance} date_deces={treeNode.attributes?.date_deces}
        statut_marital={treeNode.attributes?.statut_marital}
        photo={treeNode.attributes?.photo}
        selected={selectedId === membreId} lineageHighlighted={lineageIds.has(membreId)}
        isDark={isDark} primaryColor={primaryColor} genDepth={depth}
        onClick={() => onNodeClick(membreId)}
        onDoubleClick={() => onNodeDblClick?.(membreId)}
        onMouseEnter={() => onHover(membreId)} onMouseLeave={() => onHover(null)}
        width={w}
      />
    </foreignObject>
  );
};

// ─── Edge renderer (orienté) ───────────────────────────────────────────────────
function renderEdges(edgeGroups: EdgeGroup[], isDark: boolean, LR: boolean): React.ReactElement[] {
  const stroke = isDark ? '#1E3A1E' : '#CBD5E1';
  const result: React.ReactElement[] = [];
  edgeGroups.forEach((group, gi) => {
    const { px, py, children } = group;
    if (!children.length) return;

    if (!LR) {
      // Horizontal : descente du parent vers les enfants (axe Y)
      const childTopY = children[0].y;
      const midY = py + (childTopY - py) / 2;
      if (children.length === 1) {
        const cx = children[0].x;
        result.push(<path key={`e${gi}`} d={`M ${px} ${py} L ${px} ${midY} L ${cx} ${midY} L ${cx} ${childTopY}`} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinecap="round" />);
      } else {
        const xs = children.map(c => c.x);
        result.push(
          <line key={`ev${gi}`} x1={px} y1={py} x2={px} y2={midY} stroke={stroke} strokeWidth={1.5} strokeLinecap="round" />,
          <line key={`eh${gi}`} x1={Math.min(...xs)} y1={midY} x2={Math.max(...xs)} y2={midY} stroke={stroke} strokeWidth={1.5} />,
          ...children.map((c, ci) => <line key={`ec${gi}-${ci}`} x1={c.x} y1={midY} x2={c.x} y2={c.y} stroke={stroke} strokeWidth={1.5} strokeLinecap="round" />)
        );
      }
    } else {
      // Vertical : progression du parent vers les enfants (axe X)
      const childLeftX = children[0].x;
      const midX = px + (childLeftX - px) / 2;
      if (children.length === 1) {
        const cy = children[0].y;
        result.push(<path key={`e${gi}`} d={`M ${px} ${py} L ${midX} ${py} L ${midX} ${cy} L ${childLeftX} ${cy}`} fill="none" stroke={stroke} strokeWidth={1.5} strokeLinecap="round" />);
      } else {
        const ys = children.map(c => c.y);
        result.push(
          <line key={`eh${gi}`} x1={px} y1={py} x2={midX} y2={py} stroke={stroke} strokeWidth={1.5} strokeLinecap="round" />,
          <line key={`ev${gi}`} x1={midX} y1={Math.min(...ys)} x2={midX} y2={Math.max(...ys)} stroke={stroke} strokeWidth={1.5} />,
          ...children.map((c, ci) => <line key={`ec${gi}-${ci}`} x1={midX} y1={c.y} x2={c.x} y2={c.y} stroke={stroke} strokeWidth={1.5} strokeLinecap="round" />)
        );
      }
    }
  });
  return result;
}

// ─── Vue « liste indentée » (généalogique, compacte, imprimable) ───────────────
interface IndentedRowProps {
  node: TreeNode;
  depth: number;
  isDark: boolean;
  selectedId: number | null;
  onNodeClick: (id: number) => void;
}

const IndentedRow: React.FC<IndentedRowProps> = ({ node, depth, isDark, selectedId, onNodeClick }) => {
  const a = node.attributes;
  const couple = !!a?.isCouple;
  const guide = isDark ? '#2A3D30' : '#D8E3DC';
  const muted = isDark ? '#9CA3AF' : '#6B7280';
  const gc = genColor(depth);

  const nameSpan = (label: string, id: number, sexe?: string, deceased?: boolean) => (
    <span
      onClick={e => { e.stopPropagation(); onNodeClick(id); }}
      style={{
        cursor: 'pointer', fontWeight: 700,
        color: selectedId === id ? '#F59E0B' : (deceased ? muted : (isDark ? '#F0FDF4' : '#1C1917')),
        textDecoration: 'underline transparent', borderBottom: '1px dotted transparent',
      }}
    >
      {deceased ? '† ' : ''}{label}
    </span>
  );

  let content: React.ReactNode;
  if (couple && a?.person1 && a?.person2) {
    const p1 = a.person1, p2 = a.person2;
    const id1 = p1.attributes?.membreId ?? Number(p1.id);
    const id2 = p2.attributes?.membreId ?? Number(p2.id);
    const sym = a.isDivorced ? '💔' : a.marriageDate ? '💍' : '❤️';
    const dates = a.isDivorced
      ? [a.marriageDate && shortDate(a.marriageDate), a.divorceDate && `→ ${shortDate(a.divorceDate)}`].filter(Boolean).join(' ')
      : (a.marriageDate ? shortDate(a.marriageDate) : '');
    content = (
      <>
        {nameSpan(p1.name, id1, p1.attributes?.sexe, !!p1.attributes?.date_deces)}
        <span style={{ margin: '0 6px' }}>{sym}</span>
        {nameSpan(p2.name, id2, p2.attributes?.sexe, !!p2.attributes?.date_deces)}
        {dates && <span style={{ color: muted, fontSize: '0.82em', marginLeft: 8 }}>— {dates}</span>}
      </>
    );
  } else {
    const id = a?.membreId ?? Number(node.id);
    const y = yearsRange(a?.date_naissance, a?.date_deces);
    content = (
      <>
        {nameSpan(node.name, id, a?.sexe, !!a?.date_deces)}
        {y && <span style={{ color: muted, fontSize: '0.82em', marginLeft: 8 }}>({y})</span>}
      </>
    );
  }

  return (
    <div style={{ marginLeft: depth ? 10 : 0, borderLeft: depth ? `2px solid ${guide}` : 'none', paddingLeft: depth ? 14 : 0 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '3px 0', lineHeight: 1.4 }}>
        <span style={{ color: gc, fontSize: 13, flexShrink: 0 }}>{couple ? '◆' : '•'}</span>
        <span style={{ fontSize: 14 }}>{content}</span>
      </div>
      {node.children?.map(c => (
        <IndentedRow key={String(c.id)} node={c} depth={depth + 1} isDark={isDark} selectedId={selectedId} onNodeClick={onNodeClick} />
      ))}
    </div>
  );
};

const IndentedTree: React.FC<{ roots: TreeNode[]; isDark: boolean; selectedId: number | null; onNodeClick: (id: number) => void; }> = ({
  roots, isDark, selectedId, onNodeClick,
}) => (
  <Box sx={{
    flex: 1, overflow: 'auto', p: { xs: 2, sm: 4 },
    bgcolor: isDark ? '#0a0a0a' : '#FFFFFF',
  }}>
    <Box sx={{ maxWidth: 900, mx: 'auto' }}>
      {roots.map(r => (
        <IndentedRow key={String(r.id)} node={r} depth={0} isDark={isDark} selectedId={selectedId} onNodeClick={onNodeClick} />
      ))}
    </Box>
  </Box>
);

// ─── Main component ───────────────────────────────────────────────────────────
interface Props {
  treeData: TreeNode[];
  membres: Membre[];
  relations: Relation[];
  selectedId: number | null;
  onNodeClick: (id: number) => void;
  onEditMember?: (id: number) => void;
  onAddMember: () => void;
  focusMemberId: number | null;
  onSetFocus: (id: number | null) => void;
  active?: boolean; // true quand la vue Arbre est affichée (sinon conteneur masqué)
  onOpenExport?: () => void; // ouvre la modale d'export multi-variantes
  // Enregistre la fonction de capture de la vue à cartes (pour la variante « Cartes » de l'export)
  onRegisterCapture?: (fn: (() => Promise<{ dataUrl: string; width: number; height: number } | null>) | null) => void;
}

const FamilyTree: React.FC<Props> = ({
  treeData, membres, relations, selectedId, onNodeClick, onEditMember, onAddMember,
  focusMemberId, onSetFocus, active = true, onOpenExport, onRegisterCapture,
}) => {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const primaryColor = theme.palette.primary.main;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const containerRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const isPanning = useRef(false);
  const lastMouse = useRef({ x: 0, y: 0 });
  const lastPinchDist = useRef<number | null>(null);
  const centered = useRef(false);

  const [transform, setTransform] = useState({ x: 60, y: 60, scale: 0.9 });
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('horizontal');
  const [modeMenuAnchor, setModeMenuAnchor] = useState<null | HTMLElement>(null);

  const { nodes, edges, totalWidth, totalHeight, LR } = useMemo(() => computeLayout(treeData, layoutMode), [treeData, layoutMode]);
  const isIndented = layoutMode === 'indented';

  // Ancêtres du membre survolé
  const parentOf = useMemo(() => {
    const map = new Map<number, number[]>();
    relations.filter(r => ['parent','grand_parent','arriere_grand_parent'].includes(r.type_relation))
      .forEach(r => {
        if (!map.has(r.membre_cible)) map.set(r.membre_cible, []);
        map.get(r.membre_cible)!.push(r.membre_source);
      });
    return map;
  }, [relations]);

  const lineageIds = useMemo(() => {
    if (!hoveredId) return new Set<number>();
    const result = new Set<number>();
    const queue = [hoveredId];
    while (queue.length) {
      const curr = queue.shift()!;
      (parentOf.get(curr) || []).forEach(p => {
        if (!result.has(p)) { result.add(p); queue.push(p); }
      });
    }
    return result;
  }, [hoveredId, parentOf]);

  // Recentrage auto
  useEffect(() => {
    if (centered.current || !containerRef.current || totalWidth <= 1) return;
    const { width, height } = containerRef.current.getBoundingClientRect();
    // Conteneur masqué (autre onglet) => taille 0 : on attend qu'il soit visible
    // pour centrer, sans marquer centered (sinon centrage cassé au 1er affichage).
    if (width === 0 || height === 0) return;

    if (isMobile && !LR) {
      // Sur mobile (horizontal) : scale fixe lisible centré sur les racines (en haut)
      const rootNodes = nodes.filter(n => n.depth === 0);
      const scale = 0.75;
      let cx = totalWidth / 2;
      if (rootNodes.length > 0) {
        const minX = Math.min(...rootNodes.map(n => n.fx));
        const maxX = Math.max(...rootNodes.map(n => n.fx + n.w));
        cx = (minX + maxX) / 2;
      }
      setTransform({ x: width / 2 - cx * scale, y: 40, scale });
    } else {
      const scale = Math.min(0.9, (width - 80) / totalWidth, (height - 120) / totalHeight);
      setTransform({ x: (width - totalWidth * scale) / 2, y: 60, scale });
    }
    centered.current = true;
  }, [totalWidth, totalHeight, isMobile, nodes, active, LR]);

  useEffect(() => { centered.current = false; }, [membres.length]);

  // Re-centre lors d'un changement de modèle de disposition
  useEffect(() => { centered.current = false; }, [layoutMode]);

  // Re-centre aussi lors d'un changement de focus
  useEffect(() => { centered.current = false; }, [focusMemberId]);

  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if ((e.target as HTMLElement).closest('[data-nodeclick]')) return;
    isPanning.current = true;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    e.preventDefault();
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isPanning.current) return;
    const dx = e.clientX - lastMouse.current.x;
    const dy = e.clientY - lastMouse.current.y;
    lastMouse.current = { x: e.clientX, y: e.clientY };
    setTransform(t => ({ ...t, x: t.x + dx, y: t.y + dy }));
  }, []);

  const handleMouseUp = useCallback(() => { isPanning.current = false; }, []);

  const handleWheel = useCallback((e: WheelEvent) => {
    if (!svgRef.current) return; // vue liste : laisser le scroll natif
    e.preventDefault();
    const factor = e.deltaY > 0 ? 0.92 : 1.08;
    const rect = containerRef.current?.getBoundingClientRect();
    const mouseX = e.clientX - (rect?.left ?? 0);
    const mouseY = e.clientY - (rect?.top ?? 0);
    setTransform(t => {
      const newScale = Math.max(0.15, Math.min(2.5, t.scale * factor));
      return {
        scale: newScale,
        x: mouseX - (mouseX - t.x) * (newScale / t.scale),
        y: mouseY - (mouseY - t.y) * (newScale / t.scale),
      };
    });
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [handleWheel]);

  // Touch handlers for mobile pan + pinch-zoom
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1) {
      isPanning.current = true;
      lastMouse.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
    } else if (e.touches.length === 2) {
      isPanning.current = false;
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      lastPinchDist.current = Math.sqrt(dx * dx + dy * dy);
    }
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (e.touches.length === 1 && isPanning.current) {
      const dx = e.touches[0].clientX - lastMouse.current.x;
      const dy = e.touches[0].clientY - lastMouse.current.y;
      lastMouse.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
      setTransform(t => ({ ...t, x: t.x + dx, y: t.y + dy }));
    } else if (e.touches.length === 2 && lastPinchDist.current !== null) {
      const t0 = e.touches[0];
      const t1 = e.touches[1];
      const dx = t0.clientX - t1.clientX;
      const dy = t0.clientY - t1.clientY;
      const dist = Math.sqrt(dx * dx + dy * dy);
      const factor = dist / lastPinchDist.current;
      lastPinchDist.current = dist;
      // Point milieu entre les deux doigts (coordonnées locales au conteneur)
      const rect = containerRef.current?.getBoundingClientRect();
      const midX = (t0.clientX + t1.clientX) / 2 - (rect?.left ?? 0);
      const midY = (t0.clientY + t1.clientY) / 2 - (rect?.top ?? 0);
      setTransform(t => {
        const newScale = Math.max(0.15, Math.min(2.5, t.scale * factor));
        return {
          scale: newScale,
          x: midX - (midX - t.x) * (newScale / t.scale),
          y: midY - (midY - t.y) * (newScale / t.scale),
        };
      });
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    isPanning.current = false;
    lastPinchDist.current = null;
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onTouchMove = (e: TouchEvent) => { if (e.touches.length >= 1) e.preventDefault(); };
    el.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => el.removeEventListener('touchmove', onTouchMove);
  }, []);

  const handleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen?.().then(() => setIsFullscreen(true)).catch(() => {});
    } else {
      document.exitFullscreen?.().then(() => setIsFullscreen(false)).catch(() => {});
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  const handleCenter = () => {
    if (!containerRef.current) return;
    const { width, height } = containerRef.current.getBoundingClientRect();
    const scale = Math.min(0.9, (width - 80) / totalWidth, (height - 120) / totalHeight);
    setTransform({ x: (width - totalWidth * scale) / 2, y: 60, scale });
  };

  // Capture l'arbre ENTIER (au-delà de la zone visible) pour l'export/impression.
  // On sérialise directement le <svg> (avec ses cartes en foreignObject) en image
  // SVG, puis on la dessine sur un canvas. Cette approche rend nativement les
  // foreignObject — contrairement à html-to-image qui les imbrique et produit
  // une image blanche dans Chrome.
  const captureFullTree = useCallback(async (): Promise<{ dataUrl: string; width: number; height: number } | null> => {
    const svg = svgRef.current;
    if (!svg) return null;
    const margin = 40;
    const w = Math.ceil(totalWidth + margin * 2);
    const h = Math.ceil(totalHeight + margin * 2);
    const bg = isDark ? '#0a0a0a' : '#F9FAF5';
    const XHTML = 'http://www.w3.org/1999/xhtml';
    const SVGNS = 'http://www.w3.org/2000/svg';

    const clone = svg.cloneNode(true) as SVGSVGElement;
    const g = clone.querySelector('g');
    if (g) g.setAttribute('transform', `translate(${margin}, ${margin}) scale(1)`);
    clone.setAttribute('width', String(w));
    clone.setAttribute('height', String(h));
    clone.setAttribute('viewBox', `0 0 ${w} ${h}`);
    clone.setAttribute('xmlns', SVGNS);

    // Fond plein
    const rect = document.createElementNS(SVGNS, 'rect');
    rect.setAttribute('x', '0'); rect.setAttribute('y', '0');
    rect.setAttribute('width', String(w)); rect.setAttribute('height', String(h));
    rect.setAttribute('fill', bg);
    clone.insertBefore(rect, clone.firstChild);

    // Le contenu HTML des foreignObject doit déclarer le namespace XHTML,
    // sinon il n'est pas rendu lors de la rasterisation du SVG.
    clone.querySelectorAll('foreignObject > *').forEach(el => el.setAttribute('xmlns', XHTML));

    const xml = new XMLSerializer().serializeToString(clone);
    const svgUrl = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(xml);

    const ratio = 2; // haute résolution
    const canvas = document.createElement('canvas');
    canvas.width = w * ratio;
    canvas.height = h * ratio;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    await new Promise<void>((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
        ctx.drawImage(img, 0, 0);
        resolve();
      };
      img.onerror = () => reject(new Error('rendu SVG échoué'));
      img.src = svgUrl;
    });

    return { dataUrl: canvas.toDataURL('image/png'), width: w, height: h };
  }, [totalWidth, totalHeight, isDark]);

  // Expose la capture de la vue à cartes à la modale d'export (variante « Cartes »).
  useEffect(() => {
    onRegisterCapture?.(captureFullTree);
    return () => onRegisterCapture?.(null);
  }, [captureFullTree, onRegisterCapture]);

  // ─── Empty state ──────────────────────────────────────────────────────────
  if (membres.length === 0) {
    return (
      <Box sx={{
        flex: 1, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center', gap: 2,
        bgcolor: isDark ? alpha(primaryColor, 0.02) : '#F9FAF5',
        backgroundImage: isDark ? 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)' : 'radial-gradient(#D1FAE5 1px, transparent 1px)',
        backgroundSize: '28px 28px',
      }}>
        <TreeIcon sx={{ fontSize: 72, color: alpha(primaryColor, 0.2) }} />
        <Typography variant="h6" color="text.secondary" fontWeight={700}>Votre arbre familial est vide</Typography>
        <Typography variant="body2" color="text.disabled" textAlign="center" maxWidth={280}>
          Commencez par ajouter le premier membre de votre famille
        </Typography>
        <Box component="button" onClick={onAddMember} sx={{
          px: 3, py: 1.5, borderRadius: 3, mt: 1,
          border: `2px dashed ${alpha(primaryColor, 0.5)}`,
          bgcolor: alpha(primaryColor, 0.06), color: 'primary.main',
          fontWeight: 700, cursor: 'pointer', fontSize: '0.9rem',
          '&:hover': { bgcolor: alpha(primaryColor, 0.12) }, transition: 'background 0.2s',
        }}>
          + Ajouter le premier membre
        </Box>
      </Box>
    );
  }

  const { x: tx, y: ty, scale } = transform;
  const focusedName = focusMemberId ? membres.find(m => m.id === focusMemberId)?.prenom : null;

  const LAYOUTS: { value: LayoutMode; label: string; icon: React.ReactNode }[] = [
    { value: 'horizontal', label: 'Arbre horizontal', icon: <LayoutIcon fontSize="small" /> },
    { value: 'compact',    label: 'Compact',          icon: <CompactIcon fontSize="small" /> },
    { value: 'vertical',   label: 'Vertical (colonnes)', icon: <VerticalIcon fontSize="small" /> },
    { value: 'indented',   label: 'Liste indentée',   icon: <ListIcon fontSize="small" /> },
  ];

  const focusChip = focusMemberId && (
    <Box sx={{
      position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)',
      zIndex: 10, display: 'flex', alignItems: 'center', gap: 1,
    }}>
      <Chip
        label={`Focus : ${focusedName}`}
        onDelete={() => onSetFocus(null)}
        deleteIcon={<ExitFocusIcon />}
        size="small"
        sx={{ bgcolor: alpha(primaryColor, 0.15), color: 'primary.main', fontWeight: 700, height: 26 }}
      />
    </Box>
  );

  // Sélecteur de modèle de disposition (présent dans toutes les vues)
  const modeSelector = (
    <>
      <Paper elevation={2} sx={{ position: 'absolute', top: 12, right: 12, zIndex: 20, borderRadius: 2.5 }}>
        <Tooltip title="Disposition de l'arbre">
          <IconButton size="small" sx={{ p: 1 }} onClick={e => setModeMenuAnchor(e.currentTarget)}>
            {LAYOUTS.find(l => l.value === layoutMode)?.icon}
          </IconButton>
        </Tooltip>
      </Paper>
      <Menu anchorEl={modeMenuAnchor} open={!!modeMenuAnchor} onClose={() => setModeMenuAnchor(null)}>
        {LAYOUTS.map(l => (
          <MenuItem key={l.value} selected={layoutMode === l.value}
            onClick={() => { setLayoutMode(l.value); setModeMenuAnchor(null); }}>
            <ListItemIcon>{layoutMode === l.value ? <CheckIcon fontSize="small" /> : l.icon}</ListItemIcon>
            <ListItemText>{l.label}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  );

  // ─── Vue liste indentée ────────────────────────────────────────────────────
  if (isIndented) {
    return (
      <Box ref={containerRef} sx={{ flex: 1, position: 'relative', overflow: 'hidden', display: 'flex' }}>
        {focusChip}
        <IndentedTree roots={treeData} isDark={isDark} selectedId={selectedId} onNodeClick={onNodeClick} />
        {modeSelector}
      </Box>
    );
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        flex: 1, position: 'relative', overflow: 'hidden',
        bgcolor: isDark ? alpha(primaryColor, 0.02) : '#F9FAF5',
        backgroundImage: isDark ? 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)' : 'radial-gradient(#D1FAE5 1px, transparent 1px)',
        backgroundSize: '28px 28px',
        cursor: isPanning.current ? 'grabbing' : 'grab',
        userSelect: 'none',
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {focusChip}
      {modeSelector}

      <svg ref={svgRef} width="100%" height="100%" style={{ display: 'block' }}>
        <g transform={`translate(${tx}, ${ty}) scale(${scale})`}>
          {renderEdges(edges, isDark, LR)}
          {nodes.map(node => (
            <SvgNode
              key={String(node.id)}
              node={node}
              LR={LR}
              selectedId={selectedId}
              lineageIds={lineageIds}
              hoveredId={hoveredId}
              isDark={isDark}
              primaryColor={primaryColor}
              onNodeClick={onNodeClick}
              onNodeDblClick={onEditMember}
              onHover={setHoveredId}
            />
          ))}
        </g>
      </svg>

      {/* Zoom + actions */}
      <Paper elevation={2} sx={{
        position: 'absolute', zIndex: 20,
        ...(isMobile
          ? { bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))', left: 16, flexDirection: 'row' }
          : { bottom: 20, left: 20, flexDirection: 'column' }
        ),
        borderRadius: 2.5, overflow: 'hidden',
        display: 'flex', bgcolor: 'background.paper',
      }}>
        <Tooltip title="Zoom +" placement={isMobile ? 'top' : 'right'}>
          <IconButton size="small" sx={{ borderRadius: 0, p: 1 }}
            onClick={() => setTransform(t => ({ ...t, scale: Math.min(t.scale * 1.2, 2.5) }))}>
            <ZoomIn fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider orientation={isMobile ? 'vertical' : 'horizontal'} flexItem />
        <Tooltip title="Zoom –" placement={isMobile ? 'top' : 'right'}>
          <IconButton size="small" sx={{ borderRadius: 0, p: 1 }}
            onClick={() => setTransform(t => ({ ...t, scale: Math.max(t.scale * 0.8, 0.15) }))}>
            <ZoomOut fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider orientation={isMobile ? 'vertical' : 'horizontal'} flexItem />
        <Tooltip title="Centrer" placement={isMobile ? 'top' : 'right'}>
          <IconButton size="small" sx={{ borderRadius: 0, p: 1 }} onClick={handleCenter}>
            <CenterFocusStrong fontSize="small" />
          </IconButton>
        </Tooltip>
        {selectedId && (
          <>
            <Divider orientation={isMobile ? 'vertical' : 'horizontal'} flexItem />
            <Tooltip title={focusMemberId ? 'Quitter le focus' : 'Mode focus'} placement={isMobile ? 'top' : 'right'}>
              <IconButton size="small" sx={{ borderRadius: 0, p: 1, color: focusMemberId ? 'primary.main' : 'inherit' }}
                onClick={() => onSetFocus(focusMemberId ? null : selectedId)}>
                <FocusIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </>
        )}
        <Divider orientation={isMobile ? 'vertical' : 'horizontal'} flexItem />
        <Tooltip title="Exporter l'arbre (variantes, PDF / image)" placement={isMobile ? 'top' : 'right'}>
          <IconButton size="small" sx={{ borderRadius: 0, p: 1 }} onClick={onOpenExport}>
            <ExportIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Divider orientation={isMobile ? 'vertical' : 'horizontal'} flexItem />
        <Tooltip title={isFullscreen ? 'Quitter plein écran' : 'Plein écran'} placement={isMobile ? 'top' : 'right'}>
          <IconButton size="small" sx={{ borderRadius: 0, p: 1 }} onClick={handleFullscreen}>
            {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Paper>
    </Box>
  );
};

export default FamilyTree;
